import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  RiEditLine,
  RiDeleteBinLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiCloseLine,
} from "react-icons/ri";
import toast from "react-hot-toast";
import { confirmAndDelete } from "../../utils/confirmAndDelete";

const TestimonialCardSkeleton = () => (
  <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-200 animate-pulse">
    <div className="p-6 space-y-4">
      <div className="flex items-center space-x-4">
        <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
        <div className="space-y-2">
          <div className="h-4 bg-gray-200 rounded w-24"></div>
          <div className="h-3 bg-gray-200 rounded w-32"></div>
        </div>
      </div>
      <div className="h-4 bg-gray-200 rounded w-full"></div>
      <div className="h-4 bg-gray-200 rounded w-5/6"></div>
      <div className="h-4 bg-gray-200 rounded w-4/6"></div>
      <div className="flex space-x-1">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="w-4 h-4 bg-gray-200 rounded"></div>
        ))}
      </div>
    </div>
  </div>
);

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    designation: "",
    message: "",
  });
  const testimonialsPerPage = 9;

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/testimonial/all`
      );
      setTestimonials(res.data);
    } catch (error) {
      console.error("Error fetching testimonials:", error);
      toast.error("Failed to fetch testimonials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const indexOfLastTestimonial = currentPage * testimonialsPerPage;
  const indexOfFirstTestimonial = indexOfLastTestimonial - testimonialsPerPage;
  const currentTestimonials = testimonials.slice(
    indexOfFirstTestimonial,
    indexOfLastTestimonial
  );
  const totalPages = Math.ceil(testimonials.length / testimonialsPerPage);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const openEditTestimonialModal = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData({
      name: testimonial.user.name,
      email: testimonial.user.email,
      designation: testimonial.user.designation,
      message: testimonial.message,
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      user: {
        name: formData.name,
        email: formData.email,
        designation: formData.designation,
      },
      message: formData.message,
    };

    try {
      setIsSubmitting(true);
      if (editingTestimonial) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/testimonial/update-testimonial/${editingTestimonial._id}`,
          payload
        );
        toast.success("Testimonial updated successfully");
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/testimonial/create`,
          payload
        );
        toast.success("New testimonial created");
      }
      fetchTestimonials();
      setIsModalOpen(false);
    } catch (err) {
      console.error("Error saving testimonial:", err);
      toast.error("Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteTestimonial = async (id) => {
    const isConfirmed = await confirmAndDelete({
      id,
      url: `${process.env.REACT_APP_API_URL}/testimonial/delete-testimonial`,
    });

    if (isConfirmed) {
      try {
        await fetchTestimonials();
        const newTotalPages = Math.ceil(
          (testimonials.length - 1) / testimonialsPerPage
        );
        if (currentPage > newTotalPages) {
          setCurrentPage(newTotalPages || 1);
        }

        Swal.fire({
          title: "Deleted!",
          text: "The testimonial has been deleted.",
          icon: "success",
          confirmButtonColor: "#10B981",
        });
      } catch (err) {
        console.error("Error deleting testimonial:", err);
        Swal.fire({
          title: "Error!",
          text: "Something went wrong.",
          icon: "error",
          confirmButtonColor: "#EF4444",
        });
      }
    }
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className="min-h-screen bg-transparent p-6">
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-heading font-bold bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent">
            Testimonials
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {loading
            ? Array.from({ length: testimonialsPerPage }).map((_, i) => (
                <TestimonialCardSkeleton key={i} />
              ))
            : currentTestimonials.map((testimonial) => (
                <div
                  key={testimonial._id}
                  className="bg-white rounded-2xl overflow-hidden shadow-xl border border-primaryTextColor transition-transform hover:scale-[1.02] relative"
                >
                  <div className="absolute top-4 right-4 flex space-x-2 z-10">
                    <button
                      onClick={() => openEditTestimonialModal(testimonial)}
                      className="p-2 bg-white bg-opacity-90 rounded-lg text-primaryBtnHoverColor hover:bg-primaryBtnColor hover:text-white transition-colors shadow-md"
                    >
                      <RiEditLine size={16} />
                    </button>
                    <button
                      onClick={() => deleteTestimonial(testimonial._id)}
                      className="p-2 bg-white bg-opacity-90 rounded-lg text-red-500 hover:bg-red-500 hover:text-white transition-colors shadow-md"
                    >
                      <RiDeleteBinLine size={16} />
                    </button>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor rounded-full flex items-center justify-center text-white font-bold mr-4">
                        {testimonial.user.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-primaryTextColor">
                          {testimonial.user.name}
                        </h3>
                        <p className="text-sm text-primaryBtnHoverColor">
                          {testimonial.user.designation}
                        </p>
                        <p className="text-xs text-gray-500">
                          {testimonial.user.email}
                        </p>
                      </div>
                    </div>

                    <p className="text-secondaryTextColor mb-4 text-sm leading-relaxed">
                      "{testimonial.message}"
                    </p>

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        {formatDate(testimonial.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center items-center space-x-4 mt-8">
            <button
              onClick={() => currentPage > 1 && paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-2 rounded-lg ${
                currentPage === 1
                  ? "bg-primaryTextColor text-gray-500 cursor-not-allowed"
                  : "bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor"
              }`}
            >
              <RiArrowLeftSLine size={20} />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => paginate(page)}
                className={`px-3 py-1 rounded-lg ${
                  currentPage === page
                    ? "bg-primaryBtnColor text-white"
                    : "bg-primaryTextColor text-gray-300 hover:bg-secondaryTextColor"
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() =>
                currentPage < totalPages && paginate(currentPage + 1)
              }
              disabled={currentPage === totalPages}
              className={`p-2 rounded-lg ${
                currentPage === totalPages
                  ? "bg-primaryTextColor text-gray-500 cursor-not-allowed"
                  : "bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor"
              }`}
            >
              <RiArrowRightSLine size={20} />
            </button>
          </div>
        )}

        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-300 shadow-xl">
              <div className="sticky top-0 bg-white p-4 border-b border-gray-300 flex justify-between items-center">
                <h2 className="text-xl font-bold text-primaryTextColor">
                  {editingTestimonial
                    ? "Edit Testimonial"
                    : "Add New Testimonial"}
                </h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-500 hover:text-primaryBtnHoverColor"
                >
                  <RiCloseLine size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6">
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full bg-white border border-gray-300 rounded-lg p-3 text-primaryTextColor"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full bg-white border border-gray-300 rounded-lg p-3 text-primaryTextColor"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">
                    Designation
                  </label>
                  <input
                    type="text"
                    name="designation"
                    value={formData.designation}
                    onChange={handleInputChange}
                    className="w-full bg-white border border-gray-300 rounded-lg p-3 text-primaryTextColor"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-gray-700 mb-2">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full bg-white border border-gray-300 rounded-lg p-3 text-primaryTextColor"
                    required
                    placeholder="Enter the testimonial message..."
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="bg-gray-100 text-primaryTextColor py-2 px-4 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-primaryBtnColor text-white w-36 flex items-center justify-center py-2 px-4 rounded-lg hover:bg-primaryBtnHoverColor"
                  >
                    {isSubmitting ? (
                      <div className="spinner w-full"></div>
                    ) : editingTestimonial ? (
                      "Update"
                    ) : (
                      "Add Testimonial"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Testimonials;
